import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaLayerGroup,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaSlidersH,
  FaTag,
  FaTrash,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  formatCurrency,
  getInitialScopedOutlets,
  getInitials,
  normalizePagedRecords,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';

const createEmptyItemForm = () => ({
  category_id: '',
  item_id: '',
  metode: '',
  waktu: '',
  durasi: '',
  harga_online: '',
  harga_offline: '',
  biaya_produksi: '',
});

function PosItemsManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [metodes, setMetodes] = useState([]);
  const [waktus, setWaktus] = useState([]);
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState(createEmptyItemForm);
  const [editingId, setEditingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const categoryMap = useMemo(
    () =>
      categories.reduce((result, category) => {
        result[String(category.id)] = category.category_name || '-';
        return result;
      }, {}),
    [categories]
  );

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }

    return items.filter(item =>
      [item.product_name, item.category_name, item.metode, item.waktu]
        .some(value => String(value || '').toLowerCase().includes(query))
    );
  }, [items, search]);

  const itemSummary = useMemo(() => {
    const uniqueMethods = new Set(items.map(item => String(item.metode || '').trim()).filter(Boolean));
    const uniqueTimes = new Set(items.map(item => String(item.waktu || '').trim()).filter(Boolean));
    const averageOnline =
      items.length === 0
        ? 0
        : Math.round(items.reduce((total, item) => total + Number(item.harga_online || 0), 0) / items.length);

    return {
      total: items.length,
      methods: uniqueMethods.size,
      times: uniqueTimes.size,
      averageOnline,
    };
  }, [items]);

  const selectionLocked = Boolean(editingId);

  const resetForm = useCallback(() => {
    setEditingId('');
    setFormData(createEmptyItemForm());
    setProducts([]);
  }, []);

  const fetchReferences = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setCategories([]);
      setMetodes([]);
      setWaktus([]);
      return;
    }

    const [categoryResponse, metodeResponse, waktuResponse] = await Promise.all([
      fetch(
        `${POS_API_BASE_URL}api/categories/?outlet_id=${encodeURIComponent(outletId)}`,
        { headers: buildAuthHeaders(token) }
      ),
      fetch(`${POS_API_BASE_URL}api/metode/`, {
        headers: buildAuthHeaders(token),
      }),
      fetch(`${POS_API_BASE_URL}api/waktu/`, {
        headers: buildAuthHeaders(token),
      }),
    ]);

    const categoryData = await parseJsonResponse(categoryResponse);
    const metodeData = await parseJsonResponse(metodeResponse);
    const waktuData = await parseJsonResponse(waktuResponse);

    if (!categoryResponse.ok) {
      throw new Error(buildApiErrorMessage(categoryResponse, categoryData, 'Gagal memuat kategori item.'));
    }
    if (!metodeResponse.ok) {
      throw new Error(buildApiErrorMessage(metodeResponse, metodeData, 'Gagal memuat metode item.'));
    }
    if (!waktuResponse.ok) {
      throw new Error(buildApiErrorMessage(waktuResponse, waktuData, 'Gagal memuat waktu item.'));
    }

    setCategories((Array.isArray(categoryData) ? categoryData : []).filter(category => category.status !== false));
    setMetodes(Array.isArray(metodeData) ? metodeData : []);
    setWaktus(Array.isArray(waktuData) ? waktuData : []);
  }, []);

  const fetchProductsByCategory = useCallback(async (token, categoryId) => {
    if (!categoryId || !POS_API_BASE_URL) {
      setProducts([]);
      return;
    }

    const response = await fetch(
      `${POS_API_BASE_URL}api/products/product?cat_id=${encodeURIComponent(categoryId)}`,
      { headers: buildAuthHeaders(token) }
    );
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat daftar produk item.'));
    }

    const records = Array.isArray(data?.data) ? data.data : [];
    const serviceProducts = records.filter(product => String(product.item_type || '').toLowerCase() === 'service');
    setProducts(serviceProducts.length > 0 ? serviceProducts : records);
  }, []);

  const fetchItems = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setItems([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await fetch(
        `${POS_API_BASE_URL}api/items/?outlet_id=${encodeURIComponent(outletId)}&limit=300&page=1`,
        { headers: buildAuthHeaders(token) }
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat item POS.'));
      }

      const { records } = normalizePagedRecords(data);
      setItems(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error('Failed to fetch POS items:', error);
      setItems([]);
      setErrorMessage(error.message || 'Gagal memuat item POS.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadItemDetail = useCallback(async itemId => {
    const response = await fetch(`${POS_API_BASE_URL}api/items/detail?id=${encodeURIComponent(itemId)}`, {
      headers: buildAuthHeaders(authToken),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat detail item.'));
    }
    return data;
  }, [authToken]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;

    const initialize = async () => {
      try {
        const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
        if (!active || !token) {
          return;
        }

        setAuthToken(token);
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!active) {
          return;
        }

        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error('Failed to initialize POS items page:', error);
        if (active) {
          setErrorMessage(error.message || 'Gagal menyiapkan halaman item POS.');
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchReferences(authToken, selectedOutlet).catch(error => {
      console.error('Failed to fetch POS item references:', error);
      setErrorMessage(error.message || 'Gagal memuat referensi item POS.');
    });
    fetchItems(authToken, selectedOutlet);
  }, [authToken, fetchItems, fetchReferences, selectedOutlet]);

  useEffect(() => {
    if (!authToken || !formData.category_id || selectionLocked) {
      return;
    }

    fetchProductsByCategory(authToken, formData.category_id).catch(error => {
      console.error('Failed to fetch products by category:', error);
      setProducts([]);
      setErrorMessage(error.message || 'Gagal memuat produk untuk kategori yang dipilih.');
    });
  }, [authToken, fetchProductsByCategory, formData.category_id, selectionLocked]);

  useEffect(() => {
    resetForm();
  }, [resetForm, selectedOutlet]);

  const handleFormChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'category_id' ? { item_id: '' } : {}),
    }));
  };

  const startEdit = useCallback(async item => {
    try {
      const detail = await loadItemDetail(item.id);
      const nextCategoryId = String(detail.cat_id || item.cat_id || '');
      if (nextCategoryId) {
        await fetchProductsByCategory(authToken, nextCategoryId);
      }

      setEditingId(String(detail.id || item.id || ''));
      setFormData({
        category_id: nextCategoryId,
        item_id: String(detail.item_id || item.item_id || ''),
        metode: String(detail.metode || item.metode || ''),
        waktu: String(detail.waktu || item.waktu || ''),
        durasi: String(detail.durasi ?? item.durasi ?? ''),
        harga_online: String(detail.harga_online ?? item.harga_online ?? ''),
        harga_offline: String(detail.harga_offline ?? item.harga_offline ?? ''),
        biaya_produksi: String(detail.biaya_produksi ?? item.biaya_produksi ?? ''),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to start POS item edit:', error);
      Swal.fire('Error', error.message || 'Gagal membuka detail item.', 'error');
    }
  }, [authToken, fetchProductsByCategory, loadItemDetail]);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        outlet_id: Number(selectedOutlet),
        item_id: Number(formData.item_id),
        metode: formData.metode,
        waktu: formData.waktu,
        durasi: Number(formData.durasi || 0),
        harga_online: Number(formData.harga_online || 0),
        harga_offline: Number(formData.harga_offline || 0),
        biaya_produksi: Number(formData.biaya_produksi || 0),
      };

      const response = await fetch(
        editingId
          ? `${POS_API_BASE_URL}api/items/${editingId}`
          : `${POS_API_BASE_URL}api/items/`,
        {
          method: editingId ? 'PUT' : 'POST',
          headers: buildAuthHeaders(authToken, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menyimpan item POS.'));
      }

      await fetchItems(authToken, selectedOutlet);
      resetForm();
      Swal.fire('Success', editingId ? 'Item berhasil diperbarui.' : 'Item berhasil dibuat.', 'success');
    } catch (error) {
      console.error('Failed to save POS item:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan item POS.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async item => {
    const confirmed = await Swal.fire({
      title: 'Hapus item?',
      text: `Variant ${item.product_name || ''} akan dihapus permanen.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#d45463',
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(`${POS_API_BASE_URL}api/items/${item.id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menghapus item POS.'));
      }

      if (String(editingId) === String(item.id)) {
        resetForm();
      }
      await fetchItems(authToken, selectedOutlet);
      Swal.fire('Success', 'Item berhasil dihapus.', 'success');
    } catch (error) {
      console.error('Failed to delete POS item:', error);
      Swal.fire('Error', error.message || 'Gagal menghapus item POS.', 'error');
    }
  }, [authToken, editingId, fetchItems, resetForm, selectedOutlet]);

  const columns = useMemo(
    () => [
      {
        name: 'Product',
        sortable: true,
        grow: 1.5,
        cell: row => (
          <div className="table-identity">
            <span className="table-identity__avatar">{getInitials(row.product_name || 'IT')}</span>
            <div className="table-identity__meta">
              <strong>{row.product_name || '-'}</strong>
              <small>{row.category_name || categoryMap[String(row.cat_id)] || '-'}</small>
            </div>
          </div>
        ),
      },
      {
        name: 'Variant',
        sortable: true,
        grow: 1.1,
        cell: row => (
          <div className="pos-catalog-value-stack">
            <strong>{row.metode || '-'}</strong>
            <small>{row.waktu || '-'} • {Number(row.durasi || 0)} jam</small>
          </div>
        ),
      },
      {
        name: 'Pricing',
        sortable: true,
        grow: 1.05,
        cell: row => (
          <div className="pos-catalog-value-stack">
            <strong>{formatCurrency(row.harga_online)}</strong>
            <small>Offline {formatCurrency(row.harga_offline)}</small>
          </div>
        ),
      },
      {
        name: 'Cost',
        sortable: true,
        selector: row => Number(row.biaya_produksi || 0),
        cell: row => (
          <div className="pos-catalog-value-stack">
            <strong>{formatCurrency(row.biaya_produksi)}</strong>
            <small>Biaya produksi</small>
          </div>
        ),
      },
      {
        name: 'Actions',
        width: '180px',
        cell: row => (
          <div className="table-action-group">
            <Button variant="outline-primary" size="sm" onClick={() => startEdit(row)}>
              <FaEdit /> Edit
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
              <FaTrash /> Hapus
            </Button>
          </div>
        ),
      },
    ],
    [categoryMap, handleDelete, startEdit]
  );

  const headerActions = (
    <>
      <Form.Select
        className="accounting-outlet-picker"
        value={selectedOutlet}
        onChange={event => setSelectedOutlet(event.target.value)}
        disabled={outlets.length === 0}
      >
        {outlets.length === 0 ? <option value="">Belum ada outlet</option> : null}
        {outlets.map(outlet => (
          <option key={outlet.id} value={outlet.id}>
            {outlet.name}
          </option>
        ))}
      </Form.Select>
      <Button
        variant="outline-primary"
        onClick={() => fetchItems(authToken, selectedOutlet)}
        disabled={!authToken || !selectedOutlet || loading}
      >
        <FaRedoAlt /> Refresh
      </Button>
    </>
  );

  const headerMeta = (
    <>
      <span className="status-chip">
        <FaLayerGroup />
        {itemSummary.total} item
      </span>
      <span className="status-chip">
        <FaCheckCircle />
        {selectedOutletName}
      </span>
    </>
  );

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaSlidersH}
          eyebrow="Catalog & Inventory"
          title="Service Variants"
          description="Halaman ini dipakai untuk mengelola variasi layanan: produk jasa, metode, durasi, dan pricing variant."
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: 'Service Setup' },
            { label: 'Service Variants' },
          ]}
          actions={headerActions}
          meta={headerMeta}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-kpi-grid">
            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaSlidersH />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Total item</p>
                <strong>{itemSummary.total}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaTag />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Metode aktif</p>
                <strong>{itemSummary.methods}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaClock />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Waktu aktif</p>
                <strong>{itemSummary.times}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">Harga Online</span>
              <h2>{formatCurrency(itemSummary.averageOnline)}</h2>
              <p>Rata-rata harga online untuk variant laundry/jasa pada outlet aktif.</p>
            </div>
          </div>

          <div className="surface-card pos-catalog-note-card">
            <span className="page-eyebrow">Mapping Laundry</span>
            <h2>Flow yang dipakai</h2>
            <p>Buat dulu product bertipe <strong>Laundry / Jasa</strong> di Product Master, lalu susun variant-nya di sini memakai <strong>Laundry Methods</strong> dan <strong>Laundry Times</strong>.</p>
          </div>

          <div className="pos-catalog-layout">
            <section className="surface-card pos-catalog-form-card">
              <div className="pos-catalog-form-head">
                <div>
                  <span className="page-eyebrow">{editingId ? 'Edit' : 'Form Baru'}</span>
                  <h2>{editingId ? 'Perbarui variant laundry' : 'Tambah variant laundry'}</h2>
                  <p>Variant dipakai untuk kombinasi metode, waktu, durasi, dan harga layanan laundry/jasa.</p>
                </div>
                {editingId ? (
                  <Button variant="outline-secondary" onClick={resetForm}>
                    Reset
                  </Button>
                ) : null}
              </div>

              {selectionLocked ? (
                <p className="pos-catalog-form-lock">
                  Saat mode edit, category dan product dikunci agar perubahan hanya fokus ke variant pricing dan metode.
                </p>
              ) : null}

              <Form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <Form.Group className="field-stack span-6">
                    <Form.Label>Kategori</Form.Label>
                    <Form.Select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleFormChange}
                      required
                      disabled={selectionLocked}
                    >
                      <option value="">Pilih kategori</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.category_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="field-stack span-6">
                    <Form.Label>Produk</Form.Label>
                    <Form.Select
                      name="item_id"
                      value={formData.item_id}
                      onChange={handleFormChange}
                      required
                      disabled={selectionLocked || products.length === 0}
                    >
                      <option value="">Pilih produk</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </Form.Select>
                    <div className="pos-catalog-field-note">
                      Prioritas ditampilkan hanya product bertipe `Laundry / Jasa`. Jika kategori masih memakai setup lama, produk legacy tetap dimunculkan sebagai fallback.
                    </div>
                  </Form.Group>

                  <Form.Group className="field-stack span-4">
                    <Form.Label>Metode</Form.Label>
                    <Form.Select
                      name="metode"
                      value={formData.metode}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Pilih metode</option>
                      {metodes.map(metode => (
                        <option key={metode.id || metode.description} value={metode.description}>
                          {metode.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="field-stack span-4">
                    <Form.Label>Waktu</Form.Label>
                    <Form.Select
                      name="waktu"
                      value={formData.waktu}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Pilih waktu</option>
                      {waktus.map(waktu => (
                        <option key={waktu.id || waktu.description} value={waktu.description}>
                          {waktu.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="field-stack span-4">
                    <Form.Label>Durasi (jam)</Form.Label>
                    <Form.Control
                      name="durasi"
                      type="number"
                      min="0"
                      value={formData.durasi}
                      onChange={handleFormChange}
                      placeholder="0"
                    />
                  </Form.Group>

                  <Form.Group className="field-stack span-4">
                    <Form.Label>Harga online</Form.Label>
                    <Form.Control
                      name="harga_online"
                      type="number"
                      min="0"
                      value={formData.harga_online}
                      onChange={handleFormChange}
                      placeholder="0"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="field-stack span-4">
                    <Form.Label>Harga offline</Form.Label>
                    <Form.Control
                      name="harga_offline"
                      type="number"
                      min="0"
                      value={formData.harga_offline}
                      onChange={handleFormChange}
                      placeholder="0"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="field-stack span-4">
                    <Form.Label>Biaya produksi</Form.Label>
                    <Form.Control
                      name="biaya_produksi"
                      type="number"
                      min="0"
                      value={formData.biaya_produksi}
                      onChange={handleFormChange}
                      placeholder="0"
                    />
                  </Form.Group>
                </div>

                <div className="pos-catalog-form-actions mt-3">
                  <Button variant="outline-secondary" type="button" onClick={resetForm}>
                    Reset
                  </Button>
                  <Button type="submit" disabled={saving || !selectedOutlet || !POS_API_BASE_URL}>
                    {editingId ? <FaSave /> : <FaPlus />}
                    {saving ? 'Menyimpan...' : editingId ? 'Update Item' : 'Simpan Item'}
                  </Button>
                </div>
              </Form>
            </section>

            <section className="surface-card table-card pos-catalog-table-card">
              <div className="pos-catalog-toolbar">
                <div>
                  <span className="page-eyebrow">Data Item</span>
                  <h2 className="mb-1">Daftar variant laundry outlet</h2>
                  <p className="text-muted mb-0">
                    Variant laundry dipakai untuk memisahkan metode, waktu, durasi, dan harga per layanan.
                  </p>
                </div>

                <div className="pos-catalog-toolbar__meta">
                  <div className="search-control control-with-icon">
                    <span className="control-with-icon__icon">
                      <FaSearch />
                    </span>
                    <Form.Control
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      placeholder="Cari product, category, atau metode"
                    />
                  </div>
                  <span className="pos-catalog-filter-chip">
                    {filteredItems.length} dari {items.length} item
                  </span>
                </div>
              </div>

              <AdminDataTable
                columns={columns}
                data={filteredItems}
                progressPending={loading}
                pagination
                persistTableHead
                emptyTitle="Belum ada item POS"
                emptyDescription="Buat variant setelah product service siap agar pricing laundry tidak tercampur dengan POS jual barang."
                tableMinWidth="960px"
              />
            </section>
          </div>
        </div>
      </Container>
    </>
  );
}

export default PosItemsManager;
